import { Fragment, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { MontajBelgeCipi, MontajBirimYaniti, MontajIsGorunumu, MontajKaydiYaniti, MontajOkuma, MontajOnay, MontajRiserYaniti, MontajSatirYaniti, MontajTopluYaniti } from "@oasis/domain";
import { api } from "@/lib/api";
import { cevir } from "@/lib/dil";
import { dosyaSec, dosyaYukle, tarih, tarihSaat, useVeri } from "@/lib/veri";
import { siraliYenile } from "@/lib/mrb-toplu";
import { Band, Bos, Buton, Cip, Etiket, Hata, Kutu, Kv, Sayfa, tabloBas, tabloHucre } from "@/components/parcalar";
import { belgeLinki, sayfaAlanlari, sayfaEtiketi, usePdfSec } from "@/components/pdf-sayfa";
import {
  MONTAJ_UCU,
  MONTAJ_YENILEME_MS,
  akisRaporIstegi,
  arsivRaporIstegi,
  asmExcelKaldir,
  asmRaporKaldir,
  basiliSatirMetni,
  birimEtiketleri,
  birimIpucu,
  birimParcalari,
  celiskiListesi,
  dosyaRiserSorusu,
  eksikSayfaGorunumu,
  finishingEtiketi,
  girdiIstegi,
  girdiKaynagiAdi,
  kaldirmaSorusu,
  kaldirmayiYurut,
  kayitBandi,
  kuyrukAyrintisi,
  kuyrukDugmesi,
  kuyrukEtiketiVar,
  kuyrukSuruyor,
  listeSatiriMi,
  montajAc,
  montajGirdisiz,
  montajKaldir,
  montajSiraIslemi,
  ndtEtiketi,
  olcuAltMetni,
  olcuGruplari,
  onaySatirlari,
  raporCipOneki,
  raporYukluGorunumu,
  riserEtiketleri,
  satirCeliskileri,
  satirDurumEtiketi,
  sayfaHali,
  uyumBandi,
  uzayEtiketi,
  yuklemeyiYurut,
  type MontajGirdiYaniti,
} from "@/lib/montaj";
import { montajFormAc } from "@/lib/montaj-form";
import { MontajForm } from "./MontajForm";
import { MontajSureklilik } from "./MontajSureklilik";
import { KuyrukEtiketi } from "./SokumKuyruk";

/**
 * D-068: riserın Assembly sayfası (`/assembly/$rn`; Finishing satırındaki "Assembly" düğmesi açar). Tek veri kaynağı
 * `GET /api/montaj/riser/:rn`. Bu dosya yalnız çizer ve çağrıları yapar: sayfa hâli, etiketler, bant ve onay cümleleri, iki adımlı
 * akışların sırası `lib/montaj.ts`'tedir; düğmelerin sunulup sunulmayacağını sunucu söyler.
 * Çizilen hâller: kayıt yok (riser kuyrukta değil · kuyrukta ve rapor yok · rapor önceden yüklü) · girdisi beklenen kayıt (kuyruk
 * durumlarıyla) · okunmuş kayıt (salt okunur tam görünüm) · girdisiz tamamlanmış kayıt. Ekranda doldurulan form (kayıt türü `form`)
 * `MontajForm.tsx`'tedir: bu sayfa onu "Fill the form on screen" ile başlatır ve kayıt form türündeyse sayfanın tamamını ona
 * bırakır. Süreklilik kutusu (D-068 P3) `MontajSureklilik.tsx`'tedir; bu sayfa onu okunmuş kayıtta yalnız yerleştirir (girdisiz tamamlanmış kayıtta karşılaştırılacak birim yoktur, kutu çizilmez).
 * Kimlik yan etkisi yoktur: okunan değerler yalnız gösterilir; riserın Assembly SN'i, komponent SN'leri ve MRB bağı buradan yazılmaz.
 * İki dosya birbirini içe aktarır (bu sayfa formu çizer, form satır bloğunu buradan alır); karşılıklı adlar yalnız çizim anında
 * kullanılır, modül yüklenirken değil.
 */

const IZGARA = "grid gap-x-4 gap-y-3 sm:grid-cols-3";
const DUGME_SATIRI = "mt-3 flex flex-wrap items-center gap-2";

const hataMetni = (e: unknown): string => (e instanceof Error && e.message ? e.message : cevir("Operation failed"));

/** Süren işlem: düğmesi döner, öteki düğmeler kilitlenir. */
type Mesgul = "rapor" | "ac" | "kaldir" | "excel" | "kuyruk" | "girdi" | "girdisiz" | "form";

/** Sayfanın işlemleri; kutular yalnız çağırır. Her biri bitince sayfa sunucudan tazelenir. */
interface Islemler {
  mesgul: Mesgul | null;
  /** Akıştaki riser: var olan uç ve bütün kapıları, ardından kendiliğinden kayıt açma. */
  akisRaporYukle: () => void | Promise<void>;
  /** Akıştaki, raporu ve kaydı olmayan riser: FM-0012'yi ekranda doldurmaya başlar ("Fill the form on screen"). */
  formAc: () => void | Promise<void>;
  /** Kuyrukta olmayan riser: arşiv raporu (toplu ucun tek satırı). */
  arsivRaporYukle: () => void | Promise<void>;
  /** Rapor önceden yüklü: "Add to queue" / "Open assembly record". */
  kayitAc: () => void | Promise<void>;
  kaldir: () => void;
  excelKaldir: () => void;
  sira: (islem: "ekle" | "cikar") => void;
  girdiYukle: () => void | Promise<void>;
  girdisiz: () => void;
}

/**
 * Satırın formdaki sayfası girdide "p1" ya da "p1·p2" diye yazılır; çipte belge çipleriyle aynı dilde "p. 1" / "p. 1, 2" görünür.
 * Sayı taşımayan yazım olduğu gibi kalır.
 */
function sayfaCipi(sayfa: string): string {
  const sayilar = sayfa.match(/\d+/g);
  return sayilar ? `p. ${sayilar.join(", ")}` : sayfa;
}

/** Saklı belgenin çipi; `href` varsa belgeyi yeni sekmede açar. Uzun dosya adı dar ekranda çipin içinde kırılır. */
function BelgeCipi({ href, children }: { href: string | null; children: ReactNode }) {
  const cip = <Cip className="max-w-full break-words">{children}</Cip>;
  return href ? <a href={href} target="_blank" rel="noreferrer" className="min-w-0">{cip}</a> : <span className="min-w-0">{cip}</span>;
}

/**
 * Finishing'in tuttuğu Assembly raporunun çipi (dosya adıyla) ve varsa sayfa aralığı; belge önde kaldırılmışsa bağlantısızdır.
 * İki yerde çizilir: kayıt yokken "Assembly report" kutusunda, ve kayıt başka bir dosyadan açılmışken sarı bandın altında.
 */
function FinishingRaporCipi({ r }: { r: MontajBelgeCipi }) {
  return (
    <>
      <BelgeCipi href={r.duruyor ? belgeLinki(r.dokumanId, r.sayfa) : null}>{raporCipOneki(r)}{r.dosya}</BelgeCipi>
      {r.sayfa && <Cip>{sayfaEtiketi(r.sayfa)}</Cip>}
    </>
  );
}

/**
 * Okunmayan eski excel eki (Finishing'deki Assembly raporunun yanında duran dosya) ve yalnız onu kaldıran düğme. Var olan uç ve
 * var olan onay cümlesi kullanılır; yeni ek buradan eklenemez.
 */
function EkExcel({ v, i }: { v: MontajRiserYaniti; i: Islemler }) {
  const x = v.akis.asmRaporu?.excel;
  if (!x) return null;
  return (
    <>
      <BelgeCipi href={`/api/dokuman/${x.dokumanId}`}>{cevir("Accompanying excel (not read)")} · {x.dosya}</BelgeCipi>
      <Buton kucuk islem="evrak" mesgul={i.mesgul === "excel"} disabled={Boolean(i.mesgul)} onClick={i.excelKaldir}>{cevir("Remove excel")}</Buton>
    </>
  );
}

/** Akıştaki riserın rapor yükleme düğmesi: beş hatta NDT raporu ya da "No record available" yoksa kapalıdır (var olan kapı). */
function AkisYukleDugmesi({ v, i }: { v: MontajRiserYaniti; i: Islemler }) {
  const engel = Boolean(v.akis.ndtEngel);
  return (
    <Buton kucuk islem="evrak" tur="birincil" mesgul={i.mesgul === "rapor"} disabled={engel || Boolean(i.mesgul)} title={engel ? cevir("Blocked until every line has an NDT report or No record available") : undefined} onClick={i.akisRaporYukle}>
      {cevir("Upload Assembly report (PDF)")}
    </Buton>
  );
}

/**
 * Dönüşüm işinin durumu: kuyruk etiketi ve ayrıntısı (sırada ve dönüştürülürken yanında gri, hatada altında kırmızı zaman ve sebep).
 * Kuyruk düğmeleri kutunun düğme satırındadır.
 */
function GirdiDurumu({ k }: { k: MontajIsGorunumu }) {
  const a = kuyrukAyrintisi(k);
  return (
    <>
      <KuyrukEtiketi k={k} />
      {a && (a.ton === "bad" ? <div className="mt-1 max-w-lg break-words text-xs text-bad">{a.metin}</div> : <> <span className="text-ink-3">· {a.metin}</span></>)}
    </>
  );
}

/** Kayıt yokken "Assembly report" kutusu: riser kuyrukta değil · kuyrukta ve rapor yüklü değil · rapor önceden yüklü. */
function RaporKutusu({ v, i }: { v: MontajRiserYaniti; i: Islemler }) {
  const a = v.akis;
  const r = a.asmRaporu;
  const hal = sayfaHali(v);
  const kuyruk = <Kv ad={cevir("Assembly queue")}>{a.gonderildi && a.gonderimTs ? cevir("in the assembly queue since {0}", tarihSaat(a.gonderimTs)) : <Etiket>{cevir("not in the assembly queue")}</Etiket>}</Kv>;

  if (hal === "rapor_yuklu" && r) {
    const g = raporYukluGorunumu(r);
    const yukleyen = [r.kim, [r.tarih, r.saat].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
    return (
      <Kutu baslik={cevir("Assembly report")}>
        <div className={IZGARA}>
          {kuyruk}
          <Kv ad={cevir("Assembly report")}><Etiket tone="ok">{cevir("report uploaded")}</Etiket>{yukleyen && <> <span className="text-ink-3">· {yukleyen}</span></>}</Kv>
          <Kv ad={cevir("Assembly input")}><Etiket>{cevir("not read")}</Etiket></Kv>
        </div>
        <div className={DUGME_SATIRI}>
          <FinishingRaporCipi r={r} />
          <EkExcel v={v} i={i} />
          <Buton kucuk islem="evrak" tur="birincil" mesgul={i.mesgul === "ac"} disabled={Boolean(i.mesgul)} onClick={i.kayitAc}>{g.dugme}</Buton>
          <Buton kucuk islem="evrak" tur="tehlike" mesgul={i.mesgul === "kaldir"} disabled={Boolean(i.mesgul)} onClick={i.kaldir}>{cevir("Remove")}</Buton>
        </div>
        <Band tip="bilgi" className="mt-3">{cevir("This Assembly report was uploaded before assembly records existed; its content is not read yet. Add to queue opens the assembly record and sends the PDF to the converter.")}</Band>
        {g.uyari && <Band tip="not" className="mt-2">{g.uyari}</Band>}
      </Kutu>
    );
  }

  if (hal === "rapor_bekliyor") {
    const ndt = ndtEtiketi(a);
    return (
      <Kutu baslik={cevir("Assembly report")}>
        <div className={IZGARA}>
          {kuyruk}
          <Kv ad="NDT"><Etiket tone={ndt.ton}>{ndt.metin}</Etiket></Kv>
          <Kv ad={cevir("Assembly report")}><Etiket tone="warn">{cevir("report expected")}</Etiket></Kv>
        </div>
        {/* Sunucunun var olan cümlesi: hangi hatlarda NDT raporu eksik. */}
        {a.ndtEngel && <Band tip="not" className="mt-3">{cevir(a.ndtEngel)}</Band>}
        <div className={DUGME_SATIRI}>
          <AkisYukleDugmesi v={v} i={i} />
          {/* İkinci yol: formu ekranda doldurmak. Sunulup sunulmayacağını sunucu söyler (riser kuyrukta, rapor ve kayıt yok). */}
          {v.formAcilabilir && <Buton kucuk islem="assembly" mesgul={i.mesgul === "form"} disabled={Boolean(i.mesgul)} onClick={i.formAc}>{cevir("Fill the form on screen")}</Buton>}
        </div>
        <Band tip="bilgi" className="mt-3">{cevir("Upload the signed Assembly report (OSIUK-W-FM-0012) here. It completes the Assembly side of the Finishing step; with the Coating report the maintenance process ends. The report then joins the converter queue and its rows are read into the assembly record.")}</Band>
      </Kutu>
    );
  }

  // Riser montaj kuyruğunda değil: buradan yüklenen rapor arşiv kaydı açar. Yalnız PDF; sayfa aralığı ve resim sunulmaz.
  return (
    <Kutu baslik={cevir("Assembly report")}>
      <div className={IZGARA}>
        {kuyruk}
        <Kv ad={cevir("Assembly report")}>—</Kv>
      </div>
      <Band tip="bilgi" className="mt-3">{cevir("{0} is not in the assembly queue. A report uploaded here is kept as an archive assembly record: it is read into data, but it does not complete the Finishing step and no NDT condition is checked. To complete the Finishing step, the riser is first sent to assembly from Repair planning.", v.rn)}</Band>
      <div className={DUGME_SATIRI}>
        <Buton kucuk islem="evrak" tur="birincil" mesgul={i.mesgul === "rapor"} disabled={Boolean(i.mesgul)} onClick={i.arsivRaporYukle}>{cevir("Upload Assembly report (PDF)")}</Buton>
      </div>
    </Kutu>
  );
}

/**
 * "Assembly record" kutusu: rapor türü kaydın üç hâlinde künye, belge çipleri, düğmeler ve bant. Girdisi beklenen ve girdisiz
 * kayıtta dört hücre; okunmuş kayıtta girdiden okunan künye. Düğme sırası: kuyruk düğmesi · girdi yükleme · girdisiz tamamlama ·
 * kaldırma. Okunmuş kaydı yeniden sıraya koyan düğme çizilmez (sunucu da sunmaz); düzeltme yolu yeni girdinin yüklenmesidir.
 */
function KayitKutusu({ v, k, i }: { v: MontajRiserYaniti; k: MontajKaydiYaniti; i: Islemler }) {
  const o = k.hal === "okundu" ? k.okuma : null;
  const finishing = finishingEtiketi(v.raporUyumu);
  const bant = kayitBandi(k);
  const eksik = o ? eksikSayfaGorunumu(o.eksikSayfalar) : null;
  const uzay = o ? uzayEtiketi(o.form.assemblySnUzay) : null;
  const dugme = kuyrukDugmesi(k.kuyruk, k.kuyrugaEklenebilir, k.hal === "okundu");
  const kilitli = Boolean(i.mesgul);
  /** Kaydın raporu, belgesi önde duruyorsa (resim olarak yüklenmiş olabilir: çipin öneki belgenin türüne bakar). */
  const rapor = k.rapor?.duruyor ? k.rapor : null;
  return (
    <Kutu baslik={cevir("Assembly record")}>
      <div className={IZGARA}>
        <Kv ad={cevir("Recorded by")}>{k.acan} · {tarih(k.ts)}</Kv>
        {o ? (
          <>
            <Kv ad={cevir("Form edition")}><b>{k.basim ?? "—"}</b> <span className="text-ink-3">· {basiliSatirMetni(k.basim)}</span></Kv>
            <Kv ad={cevir("Assembly completion date (form)")}>{tarih(k.montajTarihi)}</Kv>
            <Kv ad={cevir("Assembly SN (form, as written)")}><span className="break-words">{k.assemblySnYazim || "—"}</span></Kv>
            <Kv ad={cevir("Assembly SN (resolved)")}>
              <span className="inline-flex flex-wrap items-center gap-1"><b className="min-w-0 break-words font-mono">{k.assemblySn ?? "—"}</b>{uzay && <Etiket tone={uzay.ton} className="whitespace-normal">{uzay.metin}</Etiket>}</span>
            </Kv>
            <Kv ad={cevir("Riser number (form, as written)")}><span className="break-words">{o.form.riserNoYazim || "—"}</span>{o.form.riserEtiketi && <> <Cip>TAG {o.form.riserEtiketi}</Cip></>}</Kv>
            <Kv ad={cevir("Source")}>
              <span className="inline-flex flex-wrap items-center gap-1"><Etiket tone="info">{cevir("read from document")}</Etiket><Cip className="min-w-0 max-w-full break-words">Excel · {o.kaynakDosya}{o.motor ? ` · ${cevir("engine")} ${o.motor}` : ""}</Cip></span>
            </Kv>
            {k.girdi && <Kv ad={cevir("Assembly input delivered by")}><Etiket>{girdiKaynagiAdi(k.girdi.kaynak)}</Etiket> {k.girdi.ad ?? "—"}{k.girdi.ts && <> <span className="text-ink-3">· {tarihSaat(k.girdi.ts)}</span></>}</Kv>}
            {/* Yalnız işi varsa: okunmuş kayıt yeniden dönüştürücü sırasına konmuş. */}
            {k.kuyruk.donusum && <Kv ad={cevir("Assembly input")}><GirdiDurumu k={k.kuyruk} /></Kv>}
            <Kv ad={cevir("Pages")}>{o.sayfaSayisi}{eksik && <> <Etiket tone={eksik.etiket.ton}>{eksik.etiket.metin}</Etiket></>}</Kv>
          </>
        ) : (
          <>
            <Kv ad="Assembly SN">{k.hal === "girdi_bekliyor" ? <>— <span className="text-ink-3">· {cevir("comes with the assembly input")}</span></> : (k.assemblySn ?? "—")}</Kv>
            <Kv ad={cevir("Assembly input")}>
              <GirdiDurumu k={k.kuyruk} />
              {k.hal === "girdisiz" && !k.kuyruk.donusum && <div className="mt-1 text-xs text-ink-3">{cevir("completed without the assembly input")}</div>}
            </Kv>
          </>
        )}
        {finishing && <Kv ad={cevir("Finishing step")}><Etiket tone={finishing.ton} className="whitespace-normal">{finishing.metin}</Etiket></Kv>}
      </div>
      <div className={DUGME_SATIRI}>
        {rapor && <BelgeCipi href={belgeLinki(rapor.dokumanId, rapor.sayfa)}>{raporCipOneki(rapor)}{cevir("Assembly report")}</BelgeCipi>}
        {rapor?.sayfa && <Cip>{sayfaEtiketi(rapor.sayfa)}</Cip>}
        {k.girdiBelgesi && <BelgeCipi href={`/api/dokuman/${k.girdiBelgesi.dokumanId}`}>Excel · {cevir("Assembly input")}</BelgeCipi>}
        <EkExcel v={v} i={i} />
        {dugme === "tekrar" && <Buton kucuk islem="evrak" tur="birincil" mesgul={i.mesgul === "kuyruk"} disabled={kilitli} onClick={() => i.sira("ekle")}>{cevir("Retry")}</Buton>}
        {dugme === "ekle" && <Buton kucuk islem="evrak" mesgul={i.mesgul === "kuyruk"} disabled={kilitli} onClick={() => i.sira("ekle")}>{cevir("Add to queue")}</Buton>}
        {dugme === "cikar" && <Buton kucuk islem="evrak" mesgul={i.mesgul === "kuyruk"} disabled={kilitli} onClick={() => i.sira("cikar")}>{cevir("Remove from queue")}</Buton>}
        <Buton kucuk islem="evrak" mesgul={i.mesgul === "girdi"} disabled={kilitli} onClick={i.girdiYukle}>
          {i.mesgul === "girdi" ? cevir("Reading excel…") : o ? cevir("Replace assembly input (excel)") : cevir("Upload assembly input (excel)")}
        </Buton>
        {k.girdisizTamamlanabilir && <Buton kucuk islem="evrak" mesgul={i.mesgul === "girdisiz"} disabled={kilitli} onClick={i.girdisiz}>{cevir("Proceed without excel")}</Buton>}
        {/* Kaldırılamıyorsa (dönüştürücü kaydı tutuyor) düğme kapalı çizilir; ipucu sunucunun cümlesidir. */}
        <Buton kucuk islem="evrak" tur="tehlike" mesgul={i.mesgul === "kaldir"} disabled={!k.kaldirilabilir || kilitli} title={k.kaldirmaEngeli ? cevir(k.kaldirmaEngeli) : undefined} onClick={i.kaldir}>{cevir("Remove")}</Buton>
      </div>
      {bant && <Band tip={bant.tip} className="mt-3">{bant.metin}</Band>}
      {eksik && <Band tip="not" className="mt-2">{eksik.bant}</Band>}
    </Kutu>
  );
}

/** Birimin temiz alanları: "SN 149901-4 · PN H171688-3"; değerler tek aralıklı yazıyla. */
function BirimParcalari({ b }: { b: MontajBirimYaniti }) {
  return (
    <>
      {birimParcalari(b).map((p, n) => (
        <Fragment key={p.ad}>
          <span>{n > 0 ? "· " : ""}{p.ad}</span>
          <span className="min-w-0 break-words font-mono">{p.deger}</span>
        </Fragment>
      ))}
    </>
  );
}

/** Birimin etiketleri: rol (Choke / Kill) · montajdan sonra söküldü · okuma belirsiz. */
function BirimEtiketleri({ b }: { b: MontajBirimYaniti }) {
  return <>{birimEtiketleri(b).map((e, n) => <Etiket key={n} tone={e.ton}>{e.metin}</Etiket>)}</>;
}

/** Satır bloğunun düzenleme kipinde çizilenler; ekranda doldurulan form verir (`MontajForm.tsx`). */
export interface MontajSatirDuzenleme {
  /** baş satırın sonunda: girilen birim sayısı beklenenden azsa sarı etiket */
  rozet?: ReactNode;
  /** parça numaralarının altında: durum düğmeleri, adet, birim yuvaları, Fitted By ve not girdileri */
  govde: ReactNode;
}

/**
 * FM-0012'nin bir satırı, blok düzeninde (tablo değil: dar ekranda yatay kaydırma olmasın). Sırayla: baş satır (numara, ad, basılı
 * adet, durum, Fitted By, sayfa) · parça numaraları · yazılan adet (basılıdan farklıysa) · değer hücresi yazıldığı gibi · birimler ·
 * not · çelişkiler. Liste satırlarında (cıvata 9, pul 10, locking insert 11, buoyancy 30) birimler alt alta çizilir: görünen metin
 * formdaki yazımdır, temiz alanlar altında gri durur (temiz kolon kuralın tahminidir, kanıt yazımdır). Öteki satırlarda temiz
 * alanlar yan yana durur, yazım ipucundadır (hücrenin yazımı zaten üstte görünür). Birimin notu iki düzende de ipucunda durur.
 * `listeSayfasi`: liste birimlerinin formdaki sayfası (okuma künyesinden).
 * `duzenle`: düzenleme kipi (ekranda doldurulan form). Satırın çerçevesi aynıdır (numara, ad, basılı adet, parça numaraları);
 * durum etiketinin yerine formun rozeti, okunan değerlerin yerine formun girdileri (`MontajSatirDuzenleme`) çizilir. Verilmezse
 * çizim salt okunurdur.
 */
export function MontajSatirBlogu({ x, listeSayfasi, duzenle }: { x: MontajSatirYaniti; listeSayfasi?: string | null; duzenle?: MontajSatirDuzenleme }) {
  if (duzenle) {
    return (
      <div className="border-b border-line px-3 py-2.5 last:border-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-6 text-xs text-ink-3 tnum">{x.sira}</span>
          <b className="min-w-[9rem] flex-1 break-words text-sm">{x.ad}</b>
          <span className="text-xs text-ink-3 tnum">×{x.basiliQty ?? "—"}</span>
          {duzenle.rozet}
        </div>
        <div className="ml-6 mt-0.5 break-words text-xs text-ink-3">OEM {x.oemPn ?? "—"} · OSI {x.osiPn ?? "—"}</div>
        {duzenle.govde}
      </div>
    );
  }
  const durum = satirDurumEtiketi(x.durum);
  const liste = listeSatiriMi(x.sira);
  return (
    <div className="border-b border-line px-3 py-2.5 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-6 text-xs text-ink-3 tnum">{x.sira}</span>
        <b className="min-w-[9rem] flex-1 break-words text-sm">{x.ad}</b>
        <span className="text-xs text-ink-3 tnum">×{x.basiliQty ?? "—"}</span>
        {x.basimda ? <Etiket tone={durum.ton}>{durum.metin}</Etiket> : <span className="text-xs text-ink-3">{cevir("row not printed in this edition")}</span>}
        <span className="min-w-0 break-words text-xs text-ink-3">Fitted By: {x.fittedBy ?? "—"}</span>
        {x.sayfa && <Cip>{sayfaCipi(x.sayfa)}</Cip>}
      </div>
      <div className="ml-6 mt-0.5 break-words text-xs text-ink-3">OEM {x.oemPn ?? "—"} · OSI {x.osiPn ?? "—"}</div>
      {x.qty != null && x.qty !== x.basiliQty && <div className="ml-6 text-xs text-ink-3">{cevir("Qty as written: {0}", x.qtyYazim ?? x.qty)}</div>}
      {x.deger && <div className="ml-6 mt-1 whitespace-pre-wrap break-words font-mono text-sm">{x.deger}</div>}
      {x.birimler.length > 0 && (liste ? (
        <>
          <div className="ml-6 mt-1 text-xs text-ink-3">{cevir("{0} units listed on the back page", x.birimler.length)}{listeSayfasi && <> <Cip>{sayfaCipi(listeSayfasi)}</Cip></>}</div>
          <ol className="ml-6 mt-1 space-y-1">
            {x.birimler.map((b) => (
              <li key={b.id} title={birimIpucu(b)}>
                <div className="flex flex-wrap items-center gap-1"><Etiket>{b.seq}</Etiket><span className="min-w-0 break-words font-mono text-sm">{b.yazim?.trim() || "—"}</span><BirimEtiketleri b={b} /></div>
                {birimParcalari(b).length > 0 && <div className="ml-6 flex flex-wrap items-center gap-1 text-xs text-ink-3"><BirimParcalari b={b} /></div>}
              </li>
            ))}
          </ol>
        </>
      ) : (
        <div className="ml-6 mt-2 flex flex-wrap gap-x-4 gap-y-2">
          {x.birimler.map((b) => (
            <span key={b.id} className="inline-flex min-w-0 flex-wrap items-center gap-1 text-sm" title={birimIpucu(b)}>
              <Etiket>{b.seq}</Etiket>
              {birimParcalari(b).length > 0 ? <BirimParcalari b={b} /> : <span className="min-w-0 break-words text-ink-3">{b.yazim?.trim() || "—"}</span>}
              <BirimEtiketleri b={b} />
            </span>
          ))}
        </div>
      ))}
      {x.not && <div className="ml-6 mt-1"><Cip className="max-w-full break-words">{x.not}</Cip></div>}
      {satirCeliskileri(x).map((c, n) => <Band key={n} tip="not" className="ml-6 mt-2"><b>CONFLICT</b> {c}</Band>)}
    </div>
  );
}

/**
 * "Stick outs and box gaps": iki ölçü grubu, her birinde dört hat (dar ekranda 2 × 2). Temiz değer yoksa ama formda bir şey yazıyorsa
 * "not resolved" ve altında yazım. Söküm öncesi ölçü yalnız gösterilir: fark hesabı ve tolerans uyarısı yoktur. Arka sayfa notları
 * ölçünün yanında durur (teknisyenin ölçü uyarısı orada okunmalı).
 */
function OlcuKutusu({ k, o }: { k: MontajKaydiYaniti; o: MontajOkuma }) {
  return (
    <Kutu baslik={cevir("Stick outs and box gaps")}>
      {olcuGruplari(k.olcumler, o.form.olcuBirimi).map((g, n) => (
        <Fragment key={g.tip}>
          <div className={n ? "mt-3 text-xs font-medium text-ink-3" : "text-xs font-medium text-ink-3"}>{g.baslik}</div>
          <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {g.hucreler.map(({ hat, ad, hucre }) => (
              <div key={hat} className="min-w-0">
                <div className="text-xs text-ink-3">{ad}</div>
                <div className="text-sm text-ink">{hucre.deger != null ? <span className="tnum">{hucre.deger}</span> : hucre.cozulmedi ? <Etiket tone="warn">{cevir("not resolved")}</Etiket> : "—"}</div>
                {hucre.cozulmedi && hucre.yazim && <div className="mt-0.5 break-words text-xs text-ink-3">{hucre.yazim}</div>}
                {hucre.onceki != null && <div className="mt-0.5 text-xs text-ink-3">{cevir("before disassembly: {0}", hucre.onceki)}</div>}
              </div>
            ))}
          </div>
        </Fragment>
      ))}
      <p className="mt-3 break-words text-xs text-ink-3">{olcuAltMetni(o.form)}</p>
      {o.form.arkaSayfaNotlari.length > 0 && (
        <div className="mt-3 min-w-0">
          <div className="text-xs text-ink-3">{cevir("Back-page notes (p4)")}</div>
          <ul className="list-disc break-words pl-5 text-sm text-ink">{o.form.arkaSayfaNotlari.map((m, n) => <li key={n}>{m}</li>)}</ul>
        </div>
      )}
    </Kutu>
  );
}

/**
 * "Review and approval": onay bloğunun üç satırı, formun basılı adlarıyla. Geniş tablo kendi kabında yatay kayar. Hiçbir satır
 * zorunlu değildir; boş satır uyarı üretmez.
 */
function OnayTablosu({ onay }: { onay: MontajOnay | null }) {
  return (
    <Kutu baslik={cevir("Review and approval")}>
      <div className="scroll-thin overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-line">
              <th className={tabloBas}>{cevir("Signature")}</th>
              <th className={tabloBas}>{cevir("Name (as written)")}</th>
              <th className={tabloBas}>{cevir("Date")}</th>
              <th className={tabloBas}>{cevir("Signed")}</th>
            </tr>
          </thead>
          <tbody>
            {onaySatirlari(onay).map((s) => {
              const gun = s.tarih ? tarih(s.tarih) : "—";
              return (
                <tr key={s.baslik} className="border-b border-line last:border-0">
                  <td className={tabloHucre}>{s.baslik}</td>
                  <td className={tabloHucre}>{s.ad ?? "—"}</td>
                  <td className={tabloHucre}>{gun}{s.tarihYazim && s.tarihYazim !== gun && <div className="text-xs text-ink-3">{s.tarihYazim}</div>}</td>
                  <td className={tabloHucre}>{s.imza}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Kutu>
  );
}

/**
 * Okunmuş kaydın salt okunur görünümü, kayıt kutusunun altında: çelişkiler · okuma notları · başlık · önemli ayrıntılar · 30 satır
 * ve birimleri (dört liste ilgili satırların altında) · ölçüler · teknisyen yorumları · onay bloğu. Formun basılı İngilizce
 * terimleri çevrilmez. `degisti`: Continuity kutusundaki beyan işleminden sonra sayfayı tazeler (beyan "Remove" düğmesini belirler).
 */
function OkunmusKayit({ k, o, degisti }: { k: MontajKaydiYaniti; o: MontajOkuma; degisti: () => Promise<unknown> }) {
  const celiskiler = celiskiListesi(o);
  const f = o.form;
  const baslik: [string, string | null][] = [
    ["Oil States Ref. No.", f.oilStatesRef],
    ["Customer Name", f.musteri],
    ["Customer Ref No.", f.musteriRef],
    ["Issued By", f.issuedBy],
    ["Issue Date", f.issueDateYazim],
    ["Item Description", f.kalem],
    ["Assembly Model", f.model],
    ["Max. Working Pressure", f.calismaBasinci],
    ["Assembly Part No.", f.parcaNo],
    ["Assembly Serial No.", k.assemblySnYazim],
  ];
  /** Liste birimlerinin formdaki sayfası (satırın kendi sayfasından ayrıdır: listeler arka sayfadadır). */
  const listeSayfasi = (sira: number): string | null => {
    const sayfalar = [...new Set(o.birimler.filter((b) => b.sira === sira).flatMap((b) => (b.sayfa ? [b.sayfa] : [])))];
    return sayfalar.length ? sayfalar.join(" ") : null;
  };
  return (
    <>
      {celiskiler.length > 0 && (
        <Band tip="not">
          <b>{cevir("{0} conflicts from the assembly input", celiskiler.length)}</b> {cevir("(evidence, not deleted; verified at the riser):")}
          <ul className="mt-1 list-disc break-words pl-5">{celiskiler.map((c, n) => <li key={n}>{c}</li>)}</ul>
        </Band>
      )}
      {/* Yazım servisinin okuma uyarıları (sunucu cümleleri): gösterim anında çevrilir. */}
      {o.uyarilar.length > 0 && (
        <Band tip="not">
          <b>{cevir("{0} reading notes", o.uyarilar.length)}</b> {cevir("(nothing was changed on the riser):")}
          <ul className="mt-1 list-disc break-words pl-5">{o.uyarilar.map((u, n) => <li key={n}>{cevir(u)}</li>)}</ul>
        </Band>
      )}
      <Kutu baslik={cevir("Header")}>
        <div className={IZGARA}>{baslik.map(([ad, deger]) => <Kv key={ad} ad={ad}><span className="break-words">{deger?.trim() || "—"}</span></Kv>)}</div>
      </Kutu>
      <Kutu baslik={cevir("Important details")} aksiyon={<span className="text-ink-3">Important Details Noted During Assembly</span>}>
        {k.onemliNot?.trim() ? <div className="whitespace-pre-wrap break-words text-sm">{k.onemliNot}</div> : <div className="text-sm text-ink-3">{cevir("nothing written")}</div>}
      </Kutu>
      <MontajSureklilik kayit={k} degisti={degisti} />
      <Kutu baslik={cevir("Traceability details: 30 parts")} padded={false}>
        {k.satirlar.map((x) => <MontajSatirBlogu key={x.sira} x={x} listeSayfasi={listeSayfasi(x.sira)} />)}
      </Kutu>
      <OlcuKutusu k={k} o={o} />
      <Kutu baslik={cevir("Technician's comments")}>
        {k.yorum?.trim() ? <div className="whitespace-pre-wrap break-words text-sm">{k.yorum}</div> : <div className="text-sm text-ink-3">{cevir("nothing written")}</div>}
        {f.ekIsaretler.length > 0 && (
          <Kv ad={cevir("Extra marks")} className="mt-3">
            <ul className="list-disc break-words pl-5">{f.ekIsaretler.map((e, n) => <li key={n}>{e.etiket}: {e.deger}</li>)}</ul>
          </Kv>
        )}
      </Kutu>
      <OnayTablosu onay={k.onay} />
    </>
  );
}

export function Montaj({ rn }: { rn: string }) {
  const { veri, setVeri, hata } = useVeri<MontajRiserYaniti>(MONTAJ_UCU.riser(rn));
  const [islem, setIslem] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState<Mesgul | null>(null);
  /** Akıştaki riserın raporu bugünkü ortak pencereyle seçilir (PDF + "Partial" + sayfa alanı; resim de kabul). */
  const { pdfSec, pencere } = usePdfSec();

  /**
   * Sayfanın yenilemesi: işlemden sonra ve arka planda aynı sıralı okuyucu kullanılır; aynı anda uçan iki yanıttan yalnız en son
   * başlatılanınki yazılır (geç dönen arka plan yanıtı işlemin sonucunu ezmesin).
   */
  const getir = useMemo(() => siraliYenile(() => api.get<MontajRiserYaniti>(MONTAJ_UCU.riser(rn)), setVeri), [rn, setVeri]);

  // Dönüştürücü kaydı sıraya almış ya da üstünde çalışıyorken sayfa kendiliğinden tazelenir (teslim gelince dolsun). İş yokken
  // zamanlayıcı kurulmaz; sekme gizliyken tur atlanır. Arka plan yenilemesi hata ekranı açmaz: bir sonraki turda yeniden denenir.
  const suruyor = kuyrukSuruyor(veri?.kayit?.kuyruk);
  useEffect(() => {
    if (!suruyor) return;
    const t = window.setInterval(() => {
      if (document.visibilityState !== "hidden") void getir().catch(() => undefined);
    }, MONTAJ_YENILEME_MS);
    return () => window.clearInterval(t);
  }, [suruyor, getir]);

  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;
  const v = veri;
  const k = v.kayit;
  // Ekranda doldurulan form (açık, imzalı çıktısı beklenen ya da kapalı): sayfanın tamamı formundur; aşağıdaki kutular ve
  // kayıt ile Finishing raporunun ayrışma bandı rapor türü kayıt içindir, formda çizilmez (form kendi son kutusunda söyler).
  if (k?.tur === "form") return <MontajForm v={v} setVeri={setVeri} />;

  /**
   * Bir işlemi yürütür, ardından sayfayı sunucudan tazeler (işlem düşse de: yarıda kalan iki adımlı akışın bıraktığı durum
   * görünsün). `f` gösterilecek cümleyi döndürebilir; fırlattığı hata da aynı satırda görünür.
   */
  const calis = async (ad: Mesgul, f: () => Promise<string | null | void>) => {
    setIslem(null);
    setMesgul(ad);
    let mesaj: string | null = null;
    try {
      const sonuc = await f();
      mesaj = typeof sonuc === "string" ? sonuc : null;
    } catch (e) {
      mesaj = hataMetni(e);
    }
    try {
      await getir();
    } catch (e) {
      mesaj ??= hataMetni(e);
    }
    setIslem(mesaj);
    setMesgul(null);
  };

  const i: Islemler = {
    mesgul,
    // Önce Finishing bağı (var olan uç: NDT ön koşulu ve öteki kapılar aynen), sonra montaj kaydı. Kayıt açılamazsa rapor yüklü
    // kalır ve sayfa "rapor önceden yüklü" hâline düşer; kurtarma yolu oradaki düğmedir. Sıraya girmeyen kaydın sebebi kayıt
    // kutusundaki bantta görünür, ayrıca gösterilmez.
    akisRaporYukle: async () => {
      const r = await pdfSec(cevir("Upload Assembly report (PDF)"), ".pdf,image/*");
      if (!r) return;
      const soru = dosyaRiserSorusu(r.dosya.name, v.rn);
      if (soru && !window.confirm(soru)) return;
      await calis("rapor", async () => {
        const istek = akisRaporIstegi(v.rn, r.dosya, sayfaAlanlari(r.sayfa));
        const s = await yuklemeyiYurut({
          raporYukle: () => dosyaYukle(istek.yol, istek.dosya, istek.alanlar, istek.ekDosyalar, istek.dosyaAlan),
          kayitAc: () => montajAc(v.rn),
        });
        return s.adim === "hata" ? s.mesaj : null;
      });
    },
    // Sunucu 30 satırı ve 8 ölçüyü boş açar; hiçbir alan kendiliğinden dolmaz. Tazelenen sayfa formu çizer.
    formAc: () =>
      calis("form", async () => {
        await montajFormAc(v.rn);
      }),
    arsivRaporYukle: async () => {
      const dosya = await dosyaSec(".pdf,application/pdf");
      if (!dosya) return;
      const soru = dosyaRiserSorusu(dosya.name, v.rn);
      if (soru && !window.confirm(soru)) return;
      await calis("rapor", async () => {
        const istek = arsivRaporIstegi(v.rn, dosya);
        await dosyaYukle<MontajTopluYaniti>(istek.yol, istek.dosya, istek.alanlar, istek.ekDosyalar, istek.dosyaAlan);
      });
    },
    kayitAc: () =>
      calis("ac", async () => {
        await montajAc(v.rn);
      }),
    // Önce montaj kaydı, sonra (sunucu `akisBagi` dediyse) Finishing bağı; kayıt yokken yüklü raporda yalnız bağ kalkar.
    kaldir: () => {
      const soru = kaldirmaSorusu(v);
      if (!soru || !window.confirm(soru)) return;
      void calis("kaldir", async () => {
        if (!k) {
          await asmRaporKaldir(v.rn);
          return null;
        }
        const s = await kaldirmayiYurut(k.tur, { kayitKaldir: () => montajKaldir(k.id), bagKaldir: () => asmRaporKaldir(v.rn) });
        return s.adim === "hata" ? s.mesaj : null;
      });
    },
    excelKaldir: () => {
      if (!window.confirm(cevir("Remove the excel attached to the assembly report?"))) return;
      void calis("excel", async () => {
        await asmExcelKaldir(v.rn);
      });
    },
    sira: (kuyrukIslemi) => {
      if (!k) return;
      void calis("kuyruk", async () => {
        await montajSiraIslemi(k.id, kuyrukIslemi);
      });
    },
    // Yeni girdi öncekinin yerini bütün olarak alır; okunmuş kayıtta önce sorulur. Yazımın uyarıları okuma künyesine de yazılır
    // ve tazelenen sayfada "reading notes" bandında (tek yerden) gösterilir.
    girdiYukle: async () => {
      if (!k) return;
      const excel = await dosyaSec(".xlsx");
      if (!excel) return;
      if (k.okuma && !window.confirm(cevir("Replace the rows read from the previous assembly input? The PDF stays the record's source; the change is kept in the audit log."))) return;
      await calis("girdi", async () => {
        const istek = girdiIstegi(k.id, excel);
        await dosyaYukle<MontajGirdiYaniti>(istek.yol, istek.dosya, istek.alanlar, istek.ekDosyalar, istek.dosyaAlan);
      });
    },
    girdisiz: () => {
      if (!k) return;
      if (!window.confirm(cevir("Complete the assembly record of {0} without the assembly input? The 30 rows stay empty.", v.rn))) return;
      void calis("girdisiz", async () => {
        await montajGirdisiz(k.id);
      });
    },
  };

  // Başlık etiketleri Finishing satırındakiyle aynı sözü söyler; girdisi beklenen kaydın işi varsa kuyruk etiketi de yanında.
  const etiketler = riserEtiketleri(v);
  const kuyrukEtiketi = k !== null && kuyrukEtiketiVar({ hal: k.hal, donusum: k.kuyruk.donusum });
  const uyum = k ? uyumBandi(v.raporUyumu, v.rn) : null;
  const okuma = k?.hal === "okundu" ? k.okuma : null;

  return (
    <Sayfa
      ust={<Link to="/assembly" className="normal-case tracking-normal text-orange">{cevir("← Finishing")}</Link>}
      baslik={
        <>
          {cevir("Assembly")} {v.rn}
          {etiketler.map((e, n) => <Fragment key={n}> <Etiket tone={e.ton} className={n ? "align-middle" : "ml-2 align-middle"}>{e.metin}</Etiket></Fragment>)}
          {k && kuyrukEtiketi && <> <KuyrukEtiketi k={k.kuyruk} className={etiketler.length ? "align-middle" : "ml-2 align-middle"} /></>}
        </>
      }
      alt={okuma ? cevir("OSIUK-W-FM-0012 recorded on paper; the scanned form was read into the standard assembly input excel and loaded here. Read-only.") : undefined}
    >
      <Hata mesaj={islem} />
      {/* Kayıt ile Finishing raporu ayrışmışsa kutuların üstünde sarı bant; kendiliğinden yeniden bağlama yoktur. */}
      {uyum && (
        <div>
          <Band tip="not">{uyum.metin}</Band>
          {uyum.yukleme && (
            <>
              {v.akis.ndtEngel && <Band tip="not" className="mt-2">{cevir(v.akis.ndtEngel)}</Band>}
              <div className={DUGME_SATIRI}><AkisYukleDugmesi v={v} i={i} /></div>
            </>
          )}
          {/* Finishing'in tuttuğu dosya (adımı tamamlayan rapor): kayıt kutusundaki çip kaydın kendi dosyasıdır. */}
          {uyum.finishingRaporu && v.akis.asmRaporu && <div className={DUGME_SATIRI}><FinishingRaporCipi r={v.akis.asmRaporu} /></div>}
        </div>
      )}
      {!k && <RaporKutusu v={v} i={i} />}
      {k?.tur === "rapor" && <KayitKutusu v={v} k={k} i={i} />}
      {k && okuma && <OkunmusKayit k={k} o={okuma} degisti={getir} />}
      {pencere}
    </Sayfa>
  );
}
